import React, { useCallback } from 'react';
import {
  type AddAllergyRequest,
  type UpdateAllergyRequest,
  useAddAllergyMutation,
  useRemoveAllergyMutation,
  useUpdateAllergyMutation,
} from '@features/patients';
import { useSubjectLanguageVariants } from '@features/user';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import type { Allergy } from '../types';

import { PatientAllergiesEditCard } from './PatientAllergiesEditCard';

interface PatientAllergiesEditModalProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  allergies: Allergy[];
  onAllergiesUpdated?: () => void;
  /** Source identifier for analytics */
  source: string;
}

export const PatientAllergiesEditModal: React.FC<PatientAllergiesEditModalProps> = ({
  open,
  onClose,
  patientId,
  allergies,
  onAllergiesUpdated,
  source,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { singularCap } = useSubjectLanguageVariants();
  const { patientContextBar: contextBarTracking } = useEventTracking();

  const [addAllergy, { isLoading: isAdding }] = useAddAllergyMutation();
  const [updateAllergy] = useUpdateAllergyMutation();
  const [removeAllergy] = useRemoveAllergyMutation();

  const handleAdd = useCallback(
    async (request: AddAllergyRequest): Promise<Allergy> => {
      try {
        const response = await addAllergy({
          patientId,
          addAllergyRequest: request,
        }).unwrap();
        onAllergiesUpdated?.();
        return response.allergy as Allergy;
      } catch (error) {
        contextBarTracking.saveFailed({
          patient_id: patientId,
          data_type: 'allergies',
          error_message: error instanceof Error ? error.message : 'Unknown error',
          source,
        });
        throw error;
      }
    },
    [addAllergy, patientId, onAllergiesUpdated, contextBarTracking, source],
  );

  const handleUpdate = useCallback(
    async (allergyId: string, patch: UpdateAllergyRequest): Promise<void> => {
      try {
        await updateAllergy({
          patientId,
          allergyId,
          updateAllergyRequest: patch,
        }).unwrap();
        onAllergiesUpdated?.();
      } catch (error) {
        contextBarTracking.saveFailed({
          patient_id: patientId,
          data_type: 'allergies',
          error_message: error instanceof Error ? error.message : 'Unknown error',
          source,
        });
        throw error;
      }
    },
    [updateAllergy, patientId, onAllergiesUpdated, contextBarTracking, source],
  );

  const handleDelete = useCallback(
    async (allergyId: string): Promise<void> => {
      try {
        await removeAllergy({ patientId, allergyId }).unwrap();
        onAllergiesUpdated?.();
      } catch (error) {
        contextBarTracking.saveFailed({
          patient_id: patientId,
          data_type: 'allergies',
          error_message: error instanceof Error ? error.message : 'Unknown error',
          source,
        });
        throw error;
      }
    },
    [removeAllergy, patientId, onAllergiesUpdated, contextBarTracking, source],
  );

  const handleClose = useCallback(() => {
    contextBarTracking.editModalClosed({
      patient_id: patientId,
      data_type: 'allergies',
      had_changes: false,
      source,
    });
    onClose();
  }, [onClose, patientId, source, contextBarTracking]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: fullScreen ? '100%' : '80vh',
          maxHeight: fullScreen ? '100%' : '80vh',
          bgcolor: 'background.paper',
          boxShadow: 'none',
          border: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {`Edit ${singularCap} Allergies`}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ flex: 1, overflow: 'auto', px: 2, pt: 2 }}>
          <PatientAllergiesEditCard
            allergies={allergies}
            onAddAllergy={handleAdd}
            onUpdateAllergy={handleUpdate}
            onDeleteAllergy={handleDelete}
            isLoading={isAdding}
            patientId={patientId}
            source={source}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          onClick={handleClose}
          variant="contained"
          sx={{
            borderRadius: '999px',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            bgcolor: 'primary.main',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};
